import { supabase } from '../supabase/config';
import { generateShortId } from '../utils/customer';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';

/**
 * Maps a snake_case row to camelCase object.
 */
function toCamel(row) {
    if (!row) return null;
    const result = {};
    for (const key in row) {
        const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        result[camel] = row[key];
    }
    return result;
}

/**
 * Fetches all businesses where the global customer has a record.
 * Completely DB-driven — no localStorage sessions needed.
 * 
 * @param {string} globalCustomerId - The customer ID stored as crm_global_customer_id
 * @returns {Promise<Array>} List of business objects with tenant, customer, and discount data
 */
export const fetchMyBusinesses = async (globalCustomerId) => {
    if (!globalCustomerId) return [];

    // 1. Get global customer's email
    const { data: globalRec } = await supabase
        .from('customers')
        .select('email, phone')
        .eq('id', globalCustomerId)
        .maybeSingle();

    if (!globalRec?.email) return [];

    // 2. Find ALL customer records with this email (one per tenant)
    const { data: allRecords, error: recErr } = await supabase
        .from('customers')
        .select('*')
        .eq('email', globalRec.email);

    if (recErr || !allRecords || allRecords.length === 0) return [];

    // 3. Get unique tenant IDs
    const tenantIds = [...new Set(allRecords.map(r => r.tenant_id).filter(Boolean))];

    // 4. Fetch tenants and discounts in parallel
    const [tenantsRes, discountsRes] = await Promise.all([
        supabase.from('tenants').select('*').in('id', tenantIds),
        supabase.from('discounts').select('*').in('tenant_id', tenantIds).eq('is_active', true)
    ]);

    const tenantsMap = {};
    (tenantsRes.data || []).forEach(t => { tenantsMap[t.id] = t; });

    const discountsMap = {};
    (discountsRes.data || []).forEach(d => {
        if (!discountsMap[d.tenant_id]) discountsMap[d.tenant_id] = [];
        // Filter expired
        if (d.expiry_date && new Date(d.expiry_date) < new Date()) return;
        discountsMap[d.tenant_id].push(toCamel(d));
    });

    // 5. Build result list
    const results = allRecords
        .filter(r => tenantsMap[r.tenant_id]) // Only if tenant exists
        .map(r => ({
            id: r.tenant_id,
            tenant: toCamel(tenantsMap[r.tenant_id]),
            customer: toCamel(r),
            customerId: r.id,
            discounts: discountsMap[r.tenant_id] || []
        }));

    return results;
};

/**
 * Fetches all tenants in the system (for "Yakındakiler" / Explore).
 * Excludes the "ugra" system tenant.
 */
export const fetchAllBusinesses = async () => {
    const { data } = await supabase.from('tenants').select('*').neq('slug', 'ugra');
    return (data || []).map(t => ({
        id: t.id,
        tenant: toCamel(t),
        customer: null,
        customerId: null,
        discounts: []
    }));
};

/**
 * Auto-joins a global customer to a new business (tenant).
 * Creates a local customer record with welcome points.
 * Returns the new customer data or null if already a member.
 * 
 * @param {string} globalCustomerId - The global customer ID
 * @param {string} targetTenantId - The tenant to join
 * @returns {Promise<Object|null>} The newly created customer record or null
 */
export const autoJoinBusiness = async (globalCustomerId, targetTenantId) => {
    if (!globalCustomerId || !targetTenantId) return null;

    // 1. Get global customer info
    const { data: globalRec } = await supabase
        .from('customers')
        .select('*')
        .eq('id', globalCustomerId)
        .maybeSingle();

    if (!globalRec) return null;

    // 2. Check if already a member of this tenant
    const { data: existing } = await supabase
        .from('customers')
        .select('id')
        .eq('email', globalRec.email)
        .eq('tenant_id', targetTenantId)
        .maybeSingle();

    if (existing) {
        // Already a member – return existing ID
        return { id: existing.id, alreadyMember: true };
    }

    // 3. Fetch tenant for welcome points config and loyalty logic
    const { data: tenantData } = await supabase
        .from('tenants')
        .select('loyalty_rules, loyalty_levels, visit_roles')
        .eq('id', targetTenantId)
        .maybeSingle();

    const rules = tenantData?.loyalty_rules || {};
    const welcomePoints = rules.welcomePoints || 0;
    const pointEarnings = welcomePoints > 0 ? [{ p: welcomePoints, t: new Date().toISOString() }] : [];

    // Calculate initial loyalty level and visit role
    const initialCalcData = { pointEarnings, visitTimestamps: [], purchaseCount: 0 };
    const loyaltyLevelObj = calculateLoyaltyLevel(initialCalcData, tenantData);
    const visitRoleObj = calculateVisitFrequencyRole(initialCalcData, tenantData);

    // 4. Create new customer record in this tenant
    const shortId = generateShortId();
    const { data: newCustomer, error } = await supabase
        .from('customers')
        .insert({
            first_name: globalRec.first_name,
            last_name: globalRec.last_name,
            email: globalRec.email,
            phone: globalRec.phone,
            tenant_id: targetTenantId,
            short_id: shortId,
            points: welcomePoints,
            total_points: welcomePoints,
            point_earnings: pointEarnings,
            password: globalRec.password,
            created_at: new Date().toISOString(),
            visit_role: visitRoleObj?.label || 'Yeni Üye',
            loyalty_level: loyaltyLevelObj?.label || null
        })
        .select()
        .single();

    if (error) {
        console.error('Auto-join error:', error);
        return null;
    }

    return { id: newCustomer.id, alreadyMember: false, welcomePoints, customer: toCamel(newCustomer) };
};

/**
 * Updates customer profile data across all tenant records matching the email.
 */
export const updateCustomerProfileAcrossBusinesses = async (globalCustomerId, profileData) => {
    if (!globalCustomerId) return;

    // Get email to find all records
    const { data: globalRec } = await supabase
        .from('customers')
        .select('email')
        .eq('id', globalCustomerId)
        .maybeSingle();

    if (!globalRec?.email) return;

    const updateData = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        phone: profileData.phone,
        email: profileData.email,
        birthday: profileData.birthday
    };

    // Update ALL records with matching email
    await supabase.from('customers').update(updateData).eq('email', globalRec.email);
};

/**
 * Recovers customer sessions based on verified phone numbers.
 * Returns the global customer ID if found.
 */
export const recoverCustomerSessions = async (phoneNumber, verifiedFirebasePhone) => {
    const phoneClean = phoneNumber.replace(/[^0-9]/g, '');
    const phoneWith90 = phoneClean.startsWith('90') ? phoneClean : `90${phoneClean.replace(/^0/, '')}`;
    const phoneWithPlus = `+${phoneWith90}`;

    const { data, error } = await supabase
        .from('customers')
        .select('id, email')
        .in('phone', [phoneNumber.trim(), phoneWith90, phoneWithPlus, verifiedFirebasePhone])
        .limit(1);

    if (error || !data || data.length === 0) return null;

    // Store the first match as global ID
    localStorage.setItem('crm_global_customer_id', data[0].id);
    return data[0].id;
};
